import { Big } from "big.js";
import { get, sortBy, uniq } from "lodash";

import { ColDef, ITooltipParams, ValueGetterParams } from "ag-grid-community";

import { commissionFormatter } from "lib/agGrid/formatters";
import {
  EffectiveVendorCommission,
  getVendorCommissionSourceLabel,
} from "lib/vendorCommission";

export const effectiveVendorCommissionValueGetter = ({
  data,
  colDef,
}: ValueGetterParams): string => {
  if (!data) return "";
  const value: EffectiveVendorCommission = get(data, colDef.field);
  if (!value) return "";
  const { commission } = value;
  return commission ? commissionFormatter(commission) : "";
};

const sortEffectiveVendorCommissions = (
  effectiveVendorCommissions: EffectiveVendorCommission[],
): EffectiveVendorCommission[] =>
  sortBy(effectiveVendorCommissions, ({ commission }) =>
    commission ? new Big(commission.commissionValue) : new Big(0),
  );

export const effectiveVendorCommissionArrayValueGetter = ({
  data,
  colDef,
}: ValueGetterParams): string => {
  if (!data) return "";
  const value: EffectiveVendorCommission[] = get(data, colDef.field);
  if (Array.isArray(value) && value.length > 0) {
    const sortedCommissions = sortEffectiveVendorCommissions(value);
    return uniq(
      sortedCommissions
        .map(({ commission }) => commissionFormatter(commission))
        .filter(Boolean),
    ).join(", ");
  }
  return "";
};

const formatEffectiveVendorCommissionTooltip = ({
  commission,
  source,
}: EffectiveVendorCommission): string => {
  return commission
    ? `${commissionFormatter(commission)} - ${getVendorCommissionSourceLabel(source)}`
    : "";
};

export const effectiveVendorCommissionTooltipValueGetter = ({
  data,
  colDef,
}: ITooltipParams): string => {
  if (!data) return "";
  const value: EffectiveVendorCommission = get(data, (colDef as ColDef).field);
  if (!value) return "";
  return formatEffectiveVendorCommissionTooltip(value);
};

export const effectiveVendorCommissionArrayTooltipValueGetter = ({
  data,
  colDef,
}: ITooltipParams): string => {
  if (!data) return "";
  const value: EffectiveVendorCommission[] = get(
    data,
    (colDef as ColDef).field,
  );
  if (Array.isArray(value) && value.length > 0) {
    const sortedCommissions = sortEffectiveVendorCommissions(value);
    return uniq(
      sortedCommissions
        .map(effectiveVendorCommission =>
          formatEffectiveVendorCommissionTooltip(effectiveVendorCommission),
        )
        .filter(Boolean),
    ).join(", ");
  }
  return "";
};

export const vendorCommissionBandsValueGetter = ({
  data,
  colDef,
}: ValueGetterParams): string => {
  // show multiple commissions rates as “4%, 6%, 12%“ in ascending order of percentages
  if (!data) return "";
  const value = get(data, colDef.field) || [];
  if (Array.isArray(value) && value.length > 0) {
    return sortBy(value, band => new Big(band.commissionValue))
      .map(band => commissionFormatter(band))
      .join(", ");
  }
  return "";
};
